/**
 * Draggable panel handles for Run tab: vertical (Payload | Data) and horizontal (top | trace).
 */
import React, { useCallback, useRef } from 'react';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export function VerticalResizeHandle({ onDragBegin, onDrag, className = '' }) {
  const dragging = useRef(false);

  const onPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      const row = e.currentTarget.closest('[data-runner-top-row]');
      if (!row) return;
      onDragBegin?.();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragging.current = true;
      const startX = e.clientX;
      const rect = row.getBoundingClientRect();
      const w = rect.width || 1;

      const target = e.currentTarget;
      const move = (ev) => {
        if (!dragging.current) return;
        const dx = ev.clientX - startX;
        onDrag(dx / w);
      };
      const up = (ev) => {
        dragging.current = false;
        try {
          target.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [onDrag, onDragBegin]
  );

  /* Same width as original gap-6 (1.5rem): hit zone only; visible = two thin lines */
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize Network Payload and Data panels"
      data-runner-v-grip
      onPointerDown={onPointerDown}
      className={`group relative flex w-6 shrink-0 cursor-col-resize items-stretch justify-center self-stretch z-20 touch-none select-none ${className}`}
    >
      <div className="flex h-full min-h-[3rem] items-stretch justify-center gap-0.5 py-2">
        <div className="w-px shrink-0 self-stretch rounded-full bg-gray-300 group-hover:bg-blue-500/70 transition-colors" />
        <div className="w-px shrink-0 self-stretch rounded-full bg-gray-300 group-hover:bg-blue-500/70 transition-colors" />
      </div>
    </div>
  );
}

export function HorizontalResizeHandle({ onDragBegin, onDrag, className = '' }) {
  const dragging = useRef(false);

  const onPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      const root = e.currentTarget.closest('[data-runner-root]');
      if (!root) return;
      onDragBegin?.();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragging.current = true;
      const startY = e.clientY;
      const rect = root.getBoundingClientRect();
      const h = rect.height || 1;

      const target = e.currentTarget;
      const move = (ev) => {
        if (!dragging.current) return;
        const dy = ev.clientY - startY;
        onDrag(dy / h);
      };
      const up = (ev) => {
        dragging.current = false;
        try {
          target.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [onDrag, onDragBegin]
  );

  /* Thin band aligned with side padding; hit area = full strip; visible = two horizontal lines */
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize top section and execution trace"
      data-runner-h-grip
      onPointerDown={onPointerDown}
      className={`group flex h-4 w-full shrink-0 cursor-row-resize flex-col items-stretch justify-center px-6 z-20 touch-none select-none ${className}`}
    >
      <div className="flex w-full flex-col items-stretch justify-center gap-0.5">
        <div className="h-px w-full rounded-full bg-gray-300 group-hover:bg-blue-500/70 transition-colors" />
        <div className="h-px w-full rounded-full bg-gray-300 group-hover:bg-blue-500/70 transition-colors" />
      </div>
    </div>
  );
}

export { clamp };
